// Screen: "My Profile"
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { updateProfile, uploadProfilePicture, getUserBadges, getReferralStats, getPlayerStats } from '../apiClient';
import html2canvas from 'html2canvas';
import BucksDisplay from '../components/BucksDisplay';
import CrownFrameBadge, { CARD_RANKS, SUIT_LEVELS } from '../components/CrownFrameBadge';
import StatCard from '../components/StatCard';

const US_REGIONS = [
  '', 'Northeast', 'Southeast', 'Midwest', 'Southwest', 'West Coast', 'Pacific Northwest',
  'Mid-Atlantic', 'New England', 'Great Lakes', 'Great Plains', 'Rocky Mountain', 'South'
];

function useIsMobile() {
  const [mobile, setMobile] = useState(window.innerWidth < 600);
  useEffect(() => {
    const onResize = () => setMobile(window.innerWidth < 600);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return mobile;
}

function ProfileField({ label, name, value, onChange, disabled, isMobile }) {
  return (
    <div style={{ marginBottom: isMobile ? 14 : 20 }}>
      <label style={{ display: 'block', fontSize: isMobile ? 13 : 14, color: '#8b949e', marginBottom: 6 }}>
        {label}
      </label>
      <input
        type="text"
        name={name}
        value={value}
        onChange={onChange}
        disabled={disabled}
        style={{
          width: '100%',
          padding: isMobile ? '8px 10px' : '10px 12px',
          background: disabled ? '#0d1117' : '#21262d',
          border: '1px solid #30363d',
          borderRadius: 6,
          color: '#c9d1d9',
          fontSize: 16,
          outline: 'none',
          boxSizing: 'border-box',
          cursor: disabled ? 'default' : 'text'
        }}
      />
    </div>
  );
}

export default function ProfilePage() {
  const navigate = useNavigate();
  const { user, token, updateUser, isClubMember } = useAuth();
  const isMobile = useIsMobile();
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    firstName: user?.firstName || '',
    lastName: user?.lastName || '',
    nickname: user?.nickname || '',
    zipCode: user?.zipCode || '',
    bio: user?.bio || '',
    region: user?.region || ''
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [badges, setBadges] = useState([]);
  const [referralCode, setReferralCode] = useState('');
  const [referralCopied, setReferralCopied] = useState(false);
  const [playerStats, setPlayerStats] = useState(null);
  const [showStatCard, setShowStatCard] = useState(false);
  const [shareStatus, setShareStatus] = useState('');
  const [rankTrophies, setRankTrophies] = useState([]);
  const [euchreStats, setEuchreStats] = useState(null);
  const [selectedPartner, setSelectedPartner] = useState('');
  const fileInputRef = useRef(null);
  const statCardRef = useRef(null);

  // Load user badges, referral code, player stats, and rank trophies
  useEffect(() => {
    if (token) {
      getUserBadges(token).then(data => setBadges(data.badges || []));
      getReferralStats(token).then(data => setReferralCode(data.referralCode || ''));
      if (user?.userId) {
        getPlayerStats(token, user.userId).then(data => {
          if (data) setPlayerStats(data);
        });
        // Fetch rank trophies
        fetch(`/api/trophies/${user.userId}`).then(r => r.json()).then(data => {
          setRankTrophies(data.trophies || []);
        }).catch(() => {});
        // Fetch euchre stats
        fetch(`/api/euchre/stats/${user.userId}`, {
          headers: { Authorization: `Bearer ${token}` }
        }).then(r => r.ok ? r.json() : null).then(data => {
          if (data) { setEuchreStats(data); setSelectedPartner(data.partnerStats?.[0]?.partnerId || ''); }
          else { setEuchreStats(false); }
        }).catch(() => { setEuchreStats(false); });
      }
    }
  }, [token, user?.userId]);

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setError('');
    setSuccess('');
  };

  const handleSave = async () => {
    setIsSaving(true);
    setError('');
    setSuccess('');

    try {
      const result = await updateProfile(token, formData);
      if (result.success) {
        updateUser(formData);
        setSuccess('Profile updated successfully!');
        setIsEditing(false);
      } else {
        setError(result.error || 'Failed to update profile');
      }
    } catch (err) {
      setError(err.message || 'An error occurred');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setFormData({
      firstName: user?.firstName || '',
      lastName: user?.lastName || '',
      nickname: user?.nickname || '',
      zipCode: user?.zipCode || '',
      bio: user?.bio || '',
      region: user?.region || ''
    });
    setIsEditing(false);
    setError('');
    setSuccess('');
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 500 * 1024) {
      setError('Image too large (max 500KB)');
      return;
    }

    if (!file.type.startsWith('image/')) {
      setError('Please select an image file');
      return;
    }

    setIsUploading(true);
    setError('');

    try {
      const reader = new FileReader();
      reader.onload = async () => {
        const result = await uploadProfilePicture(token, reader.result);
        if (result.success) {
          updateUser({ profilePictureUrl: result.profilePictureUrl });
          setSuccess('Profile picture updated!');
        } else {
          setError(result.error || 'Failed to upload picture');
        }
        setIsUploading(false);
      };
      reader.onerror = () => {
        setError('Failed to read image file');
        setIsUploading(false);
      };
      reader.readAsDataURL(file);
    } catch (err) {
      setError(err.message || 'Upload failed');
      setIsUploading(false);
    }

    // Reset file input so same file can be re-selected
    e.target.value = '';
  };

  const avatarUrl = user?.profilePictureUrl;
  const initials = ((user?.firstName?.[0] || '') + (user?.lastName?.[0] || '')).toUpperCase() || '?';

  return (
    <div style={{
      padding: isMobile ? '20px 16px 40px' : '60px 32px',
      maxWidth: 600, margin: '0 auto',
      fontFamily: "'Georgia', serif", color: '#e2e8f0'
    }}>
      <h1 style={{ fontSize: isMobile ? 24 : 36, fontWeight: 700, marginBottom: isMobile ? 16 : 24 }}>
        My Profile
      </h1>

      {error && (
        <div style={{
          background: '#3d1f1f', border: '1px solid #f85149', color: '#f85149',
          padding: '12px 16px', borderRadius: 6, marginBottom: 20, fontSize: isMobile ? 13 : 14
        }}>
          {error}
        </div>
      )}

      {success && (
        <div style={{
          background: '#1c3d2a', border: '1px solid #3fb950', color: '#3fb950',
          padding: '12px 16px', borderRadius: 6, marginBottom: 20, fontSize: isMobile ? 13 : 14
        }}>
          {success}
        </div>
      )}

      <div style={{
        background: '#161b22', borderRadius: 12,
        padding: isMobile ? 20 : 32,
        border: '1px solid #30363d', marginBottom: 24
      }}>
        {/* Profile Picture + Balance */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: isMobile ? 16 : 24,
          marginBottom: isMobile ? 20 : 32,
          paddingBottom: isMobile ? 16 : 24, borderBottom: '1px solid #30363d'
        }}>
          {/* Avatar */}
          <div style={{ position: 'relative', flexShrink: 0 }}>
            <div
              onClick={() => fileInputRef.current?.click()}
              style={{
                width: isMobile ? 72 : 90,
                height: isMobile ? 72 : 90,
                borderRadius: '50%',
                background: avatarUrl ? 'none' : 'linear-gradient(135deg, #238636, #1a6b2a)',
                border: isClubMember ? '3px solid #3fb950' : '3px solid #30363d',
                boxShadow: isClubMember ? '0 0 12px rgba(63,185,80,0.4)' : 'none',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                overflow: 'hidden',
                position: 'relative'
              }}
              title="Click to change photo"
            >
              {avatarUrl ? (
                <img src={avatarUrl} alt="Profile" style={{
                  width: '100%', height: '100%', objectFit: 'cover'
                }} />
              ) : (
                <span style={{
                  fontSize: isMobile ? 24 : 30, fontWeight: 700, color: '#fff'
                }}>{initials}</span>
              )}
              {/* Hover overlay */}
              <div style={{
                position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.5)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                opacity: 0, transition: 'opacity 0.2s',
                borderRadius: '50%'
              }}
                onMouseEnter={e => e.currentTarget.style.opacity = 1}
                onMouseLeave={e => e.currentTarget.style.opacity = 0}
              >
                <span style={{ color: '#fff', fontSize: 11, fontWeight: 600 }}>
                  {isUploading ? 'Uploading...' : 'Change'}
                </span>
              </div>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handlePhotoUpload}
              style={{ display: 'none' }}
            />
          </div>

          {/* Balance + ELO */}
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 8 }}>
            <div style={{
              background: '#0d1117', borderRadius: 8,
              padding: isMobile ? 10 : 14, textAlign: 'center'
            }}>
              <div style={{ color: '#8b949e', fontSize: 12, marginBottom: 4 }}>Bucks Balance</div>
              <BucksDisplay amount={user?.playMoneyBalance || 0} size="lg" />
            </div>
            <div style={{
              background: '#0d1117', borderRadius: 8,
              padding: isMobile ? 10 : 14, textAlign: 'center'
            }}>
              <div style={{ color: '#8b949e', fontSize: 12, marginBottom: 6 }}>Rank</div>
              <CrownFrameBadge
                cardRankId={user?.cardRankId || user?.permanentCardRank}
                suitSymbol={user?.suitSymbol || user?.preferredSuit || '♣'}
                points={user?.activeRP ?? user?.rankPoints ?? 0}
                size={isMobile ? 'sm' : 'lg'}
              />
              <div style={{ color: '#c9d1d9', fontSize: isMobile ? 13 : 15, fontWeight: 700, marginTop: 8, fontFamily: "'Georgia', serif" }}>
                {user?.displayRank || `${CARD_RANKS.find(r => r.id === (user?.cardRankId || 1))?.name || 'Nine'} of ${SUIT_LEVELS.find(s => s.symbol === (user?.suitSymbol || '♣'))?.name || 'Clubs'}`}
              </div>

              {/* Suit Level Progression Bar */}
              <div style={{ marginTop: 10, padding: '0 4px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 4 }}>
                  {SUIT_LEVELS.map(sl => {
                    const current = (user?.suitLevel || 1) >= sl.level;
                    const active = (user?.suitLevel || 1) === sl.level;
                    const suitColors = { 1: '#1A5C34', 2: '#C62828', 3: '#1A1A1A', 4: '#D4AF37' };
                    return (
                      <div key={sl.level} style={{
                        flex: 1, textAlign: 'center', padding: '4px 2px',
                        background: active ? suitColors[sl.level] + '30' : 'transparent',
                        borderRadius: 6,
                        border: active ? `2px solid ${suitColors[sl.level]}` : '1px solid transparent',
                        transition: 'all 0.2s',
                      }}>
                        <div style={{
                          fontSize: isMobile ? 16 : 20,
                          color: current ? suitColors[sl.level] : '#30363d',
                          opacity: current ? 1 : 0.4,
                        }}>{sl.symbol}</div>
                        <div style={{
                          fontSize: 9, color: current ? '#8b949e' : '#30363d',
                          fontWeight: active ? 700 : 400,
                        }}>{sl.name}</div>
                      </div>
                    );
                  })}
                </div>
                {user?.nextSuitLevel && (
                  <div style={{ fontSize: 10, color: '#58a6ff', marginTop: 4 }}>
                    {user.activeToNextSuit} RP to {user.nextSuitLevel}
                  </div>
                )}
              </div>

              {/* Preferred Suit Selector */}
              <div style={{ display: 'flex', justifyContent: 'center', gap: 6, marginTop: 10 }}>
                {['♠', '♥', '♦', '♣'].map(suit => {
                  const selected = (user?.preferredSuit || '♠') === suit;
                  const isRed = suit === '♥' || suit === '♦';
                  return (
                    <button key={suit} onClick={async () => {
                      if (selected) return;
                      updateUser({ preferredSuit: suit });
                      try { await updateProfile(token, { preferredSuit: suit }); } catch(e) {}
                    }} style={{
                      width: 28, height: 28, borderRadius: 6,
                      background: selected ? '#21262d' : 'transparent',
                      border: selected ? '2px solid #d4af37' : '1px solid #30363d',
                      color: isRed ? '#dc2626' : '#c9d1d9',
                      fontSize: 16, cursor: 'pointer',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>{suit}</button>
                  );
                })}
              </div>

              {/* Lifetime RP + Next Card Rank */}
              <div style={{ marginTop: 8, fontSize: 10, color: '#8b949e' }}>
                Lifetime RP: {(user?.lifetimeRP || 0).toLocaleString()}
                {user?.nextCardRank && (
                  <span> &middot; {(user?.lifetimeToNextRank || 0).toLocaleString()} to {user.nextCardRank}</span>
                )}
              </div>
            </div>
            {user?.region && (
              <div style={{
                background: '#0d1117', borderRadius: 8,
                padding: isMobile ? 8 : 10, textAlign: 'center'
              }}>
                <div style={{ color: '#8b949e', fontSize: 12, marginBottom: 4 }}>Region</div>
                <div style={{ color: '#58a6ff', fontSize: isMobile ? 12 : 14, fontWeight: 600 }}>
                  {user.region}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Profile Fields */}
        <div style={{ marginBottom: isMobile ? 14 : 20 }}>
          <label style={{ display: 'block', fontSize: isMobile ? 13 : 14, color: '#8b949e', marginBottom: 6 }}>
            Email
          </label>
          <div style={{
            padding: isMobile ? '8px 10px' : '10px 12px',
            background: '#0d1117', border: '1px solid #30363d',
            borderRadius: 6, color: '#c9d1d9', fontSize: isMobile ? 14 : 15
          }}>
            {user?.email}
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: isMobile ? 0 : 16 }}>
          <ProfileField label="First Name" name="firstName" value={formData.firstName}
            onChange={handleInputChange} disabled={!isEditing} isMobile={isMobile} />
          <ProfileField label="Last Name" name="lastName" value={formData.lastName}
            onChange={handleInputChange} disabled={!isEditing} isMobile={isMobile} />
        </div>

        <ProfileField label="Nickname" name="nickname" value={formData.nickname}
          onChange={handleInputChange} disabled={!isEditing} isMobile={isMobile} />

        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: isMobile ? 0 : 16 }}>
          <ProfileField label="Zip Code" name="zipCode" value={formData.zipCode}
            onChange={handleInputChange} disabled={!isEditing} isMobile={isMobile} />

          {/* Region Dropdown */}
          <div style={{ marginBottom: isMobile ? 14 : 20 }}>
            <label style={{ display: 'block', fontSize: isMobile ? 13 : 14, color: '#8b949e', marginBottom: 6 }}>
              Region
            </label>
            <select
              name="region"
              value={formData.region}
              onChange={handleInputChange}
              disabled={!isEditing}
              style={{
                width: '100%',
                padding: isMobile ? '8px 10px' : '10px 12px',
                background: !isEditing ? '#0d1117' : '#21262d',
                border: '1px solid #30363d',
                borderRadius: 6,
                color: '#c9d1d9',
                fontSize: 16,
                outline: 'none',
                boxSizing: 'border-box',
                cursor: !isEditing ? 'default' : 'pointer',
                appearance: isEditing ? 'auto' : 'none'
              }}
            >
              <option value="">Select region...</option>
              {US_REGIONS.filter(r => r).map(r => (
                <option key={r} value={r}>{r}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Bio */}
        <div style={{ marginBottom: isMobile ? 14 : 20 }}>
          <label style={{ display: 'block', fontSize: isMobile ? 13 : 14, color: '#8b949e', marginBottom: 6 }}>
            Bio
          </label>
          <textarea
            name="bio"
            value={formData.bio}
            onChange={handleInputChange}
            disabled={!isEditing}
            maxLength={200}
            placeholder={isEditing ? "Tell us about yourself..." : ""}
            rows={3}
            style={{
              width: '100%',
              padding: isMobile ? '8px 10px' : '10px 12px',
              background: !isEditing ? '#0d1117' : '#21262d',
              border: '1px solid #30363d',
              borderRadius: 6,
              color: '#c9d1d9',
              fontSize: 16,
              outline: 'none',
              boxSizing: 'border-box',
              cursor: !isEditing ? 'default' : 'text',
              resize: 'none',
              fontFamily: 'inherit'
            }}
          />
          {isEditing && (
            <div style={{ textAlign: 'right', fontSize: 11, color: '#484f58', marginTop: 2 }}>
              {formData.bio.length}/200
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div style={{ display: 'flex', gap: 12, marginTop: isMobile ? 16 : 24 }}>
          {isEditing ? (
            <>
              <button onClick={handleSave} disabled={isSaving} style={{
                flex: 1, background: '#238636', color: '#fff', border: 'none',
                padding: isMobile ? '10px 16px' : '12px 20px', borderRadius: 6,
                fontSize: isMobile ? 14 : 15, fontWeight: 600,
                cursor: isSaving ? 'not-allowed' : 'pointer',
                opacity: isSaving ? 0.7 : 1
              }}>
                {isSaving ? 'Saving...' : 'Save Changes'}
              </button>
              <button onClick={handleCancel} disabled={isSaving} style={{
                padding: isMobile ? '10px 16px' : '12px 20px',
                background: 'transparent', border: '1px solid #30363d', color: '#c9d1d9',
                borderRadius: 6, fontSize: isMobile ? 14 : 15, cursor: 'pointer'
              }}>
                Cancel
              </button>
            </>
          ) : (
            <button onClick={() => setIsEditing(true)} style={{
              flex: 1, background: '#21262d', color: '#c9d1d9',
              border: '1px solid #30363d', padding: isMobile ? '10px 16px' : '12px 20px',
              borderRadius: 6, fontSize: isMobile ? 14 : 15, fontWeight: 600, cursor: 'pointer'
            }}>
              Edit Profile
            </button>
          )}
        </div>
      </div>

      {/* Lifetime Stats */}
      <div style={{
        background: '#161b22', borderRadius: 12,
        padding: isMobile ? 20 : 32,
        border: '1px solid #30363d', marginBottom: 24
      }}>
        <h2 style={{ fontSize: isMobile ? 18 : 22, fontWeight: 700, marginBottom: isMobile ? 12 : 20, color: '#d4af37' }}>
          Lifetime Stats
        </h2>
        <div style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)',
          gap: isMobile ? 10 : 14
        }}>
          {[
            { label: 'Total Won', value: `$${(user?.totalBucksWon || 0).toLocaleString()}` },
            { label: 'Lifetime P&L', value: `${(user?.lifetimeNetProfit || 0) >= 0 ? '+' : ''}$${(user?.lifetimeNetProfit || 0).toLocaleString()}`, color: (user?.lifetimeNetProfit || 0) >= 0 ? '#3fb950' : '#f85149' },
            { label: 'Peak Rank', value: CARD_RANKS.find(r => r.id === (user?.permanentCardRank || 1))?.name || 'Nine' },
            { label: 'Games Played', value: playerStats?.gameStats?.gamesPlayed?.toLocaleString() || '0' },
            { label: 'Active RP', value: (user?.activeRP || 0).toLocaleString() },
            { label: 'Lifetime RP', value: (user?.lifetimeRP || 0).toLocaleString() },
          ].map((stat, i) => (
            <div key={i} style={{
              background: '#0d1117', borderRadius: 8, padding: '14px 12px',
              border: '1px solid #30363d', textAlign: 'center'
            }}>
              <div style={{ fontSize: 11, color: '#8b949e', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>{stat.label}</div>
              <div style={{ fontSize: isMobile ? 16 : 18, fontWeight: 700, color: stat.color || '#c9d1d9' }}>{stat.value}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Rank Trophy Room */}
      <div style={{
        background: '#161b22', borderRadius: 12,
        padding: isMobile ? 20 : 32,
        border: '1px solid #30363d', marginBottom: 24
      }}>
        <h2 style={{ fontSize: isMobile ? 18 : 22, fontWeight: 700, marginBottom: isMobile ? 12 : 20, color: '#d4af37' }}>
          Rank Trophy Room
        </h2>
        <div style={{
          display: 'flex', flexWrap: 'wrap', justifyContent: 'center',
          gap: isMobile ? 8 : 12,
        }}>
          {CARD_RANKS.map(rank => {
            const earned = rankTrophies.find(t => t.trophyType === 'card_rank_promotion' && t.cardRank === rank.id);
            const isCurrentOrBelow = (user?.permanentCardRank || 1) >= rank.id;
            return (
              <div key={rank.id} style={{
                textAlign: 'center', padding: isMobile ? 6 : 10,
                background: earned ? '#0d1117' : '#0d111733',
                borderRadius: 10,
                border: earned ? '1px solid #d4af37' : '1px solid #21262d',
                opacity: earned || isCurrentOrBelow ? 1 : 0.4,
                minWidth: isMobile ? 60 : 80,
                position: 'relative',
              }}>
                {earned || isCurrentOrBelow ? (
                  <CrownFrameBadge
                    cardRankId={rank.id}
                    suitSymbol={user?.preferredSuit || '♠'}
                    showPoints={false}
                    size="sm"
                  />
                ) : (
                  <div style={{
                    width: isMobile ? 38 : 42, height: isMobile ? 52 : 58,
                    background: '#21262d', borderRadius: 6,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    margin: '0 auto', border: '2px solid #30363d',
                  }}>
                    <span style={{ color: '#484f58', fontSize: 14, fontWeight: 700 }}>???</span>
                  </div>
                )}
                <div style={{ fontSize: 9, color: earned ? '#d4af37' : '#484f58', marginTop: 4, fontWeight: 600 }}>
                  {rank.name}
                </div>
                {!isCurrentOrBelow && (
                  <div style={{ fontSize: 8, color: '#484f58', marginTop: 2 }}>
                    {rank.lifetimeRpRequired.toLocaleString()} RP
                  </div>
                )}
                {earned?.snapshotData && (
                  <div style={{ fontSize: 8, color: '#8b949e', marginTop: 2 }}>
                    {new Date(earned.achievedAt).toLocaleDateString()}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Trophy Case */}
      <div style={{
        background: '#161b22', borderRadius: 12,
        padding: isMobile ? 20 : 32,
        border: '1px solid #30363d', marginBottom: 24
      }}>
        <h2 style={{ fontSize: isMobile ? 18 : 22, fontWeight: 700, marginBottom: isMobile ? 12 : 20, color: '#d4af37' }}>
          Trophy Case
        </h2>
        {badges.length === 0 ? (
          <div style={{ color: '#484f58', fontSize: 14, textAlign: 'center', padding: '20px 0' }}>
            No badges earned yet. Keep playing to unlock achievements!
          </div>
        ) : (
          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(3, 1fr)',
            gap: isMobile ? 10 : 14
          }}>
            {badges.map(badge => {
              const rarityColors = {
                common: '#8b949e',
                uncommon: '#3fb950',
                rare: '#58a6ff',
                legendary: '#d4af37'
              };
              const borderColor = rarityColors[badge.rarity] || '#30363d';
              return (
                <div key={badge.badge_id} style={{
                  background: '#0d1117',
                  border: `1px solid ${borderColor}`,
                  borderRadius: 10,
                  padding: isMobile ? '12px 8px' : '16px 12px',
                  textAlign: 'center',
                  position: 'relative'
                }}>
                  <div style={{ fontSize: isMobile ? 28 : 36, marginBottom: 6 }}>{badge.icon}</div>
                  <div style={{
                    fontSize: isMobile ? 12 : 13, fontWeight: 600, color: '#c9d1d9',
                    marginBottom: 4, lineHeight: 1.3
                  }}>{badge.name}</div>
                  <div style={{
                    fontSize: 10, color: '#484f58', lineHeight: 1.3
                  }}>{badge.description}</div>
                  <div style={{
                    fontSize: 9, color: borderColor, fontWeight: 600,
                    textTransform: 'uppercase', marginTop: 6, letterSpacing: 0.5
                  }}>{badge.rarity}</div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Hand Performance */}
      {playerStats && (
        <div style={{
          background: '#161b22', borderRadius: 12,
          padding: isMobile ? 20 : 32,
          border: '1px solid #30363d', marginBottom: 24
        }}>
          <h2 style={{ fontSize: isMobile ? 18 : 22, fontWeight: 700, marginBottom: isMobile ? 12 : 20, color: '#58a6ff' }}>
            Hand Performance
          </h2>
          {/* Trump Call Success Rate */}
          {(() => {
            const calls = playerStats.trumpStats?.totalCalls || 0;
            const successes = playerStats.trumpStats?.successfulCalls || 0;
            const rate = calls > 0 ? Math.round((successes / calls) * 100) : null;
            const barColor = rate === null ? '#30363d' : rate >= 60 ? '#3fb950' : rate >= 45 ? '#f59e0b' : '#ef4444';
            return (
              <div style={{ marginBottom: 18 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 6 }}>
                  <span style={{ fontSize: 13, color: '#c9d1d9', fontWeight: 600 }}>Trump Call Success</span>
                  <span style={{ fontSize: 13, color: barColor, fontWeight: 700 }}>
                    {rate === null ? 'No data yet' : `${rate}%`}
                    {calls > 0 && <span style={{ fontSize: 11, color: '#8b949e', fontWeight: 400, marginLeft: 6 }}>{calls.toLocaleString()} calls</span>}
                  </span>
                </div>
                <div style={{ background: '#21262d', borderRadius: 6, height: 12, overflow: 'hidden', position: 'relative' }}>
                  <div style={{
                    width: `${Math.min(100, rate || 0)}%`, height: '100%',
                    background: `linear-gradient(90deg, ${barColor}88, ${barColor})`,
                    borderRadius: 6, transition: 'width 0.6s ease'
                  }} />
                  {rate !== null && (
                    <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, display: 'flex', alignItems: 'center' }}>
                      {Array.from({ length: Math.min(calls, 50) }).map((_, i) => (
                        <div key={i} style={{
                          flex: 1, height: '100%',
                          borderRight: i < Math.min(calls, 50) - 1 ? '1px solid rgba(0,0,0,0.2)' : 'none'
                        }} />
                      ))}
                    </div>
                  )}
                </div>
                <div style={{ fontSize: 10, color: '#484f58', marginTop: 4 }}>
                  Each segment = 1 trump call · Bar grows with every hand you play
                </div>
              </div>
            );
          })()}

          {/* Forced Spade Play (when dealer auto-plays spades) */}
          {(() => {
            const playDecisions = playerStats.playStats || {};
            const playCount = playDecisions.play?.count || 0;
            const sitCount = playDecisions.sit?.count || 0;
            const total = playCount + sitCount;
            const playRate = total > 0 ? Math.round((playCount / total) * 100) : null;
            if (total === 0) return null;
            return (
              <div style={{ marginBottom: 6 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 6 }}>
                  <span style={{ fontSize: 13, color: '#c9d1d9', fontWeight: 600 }}>Play-or-Sit Decisions</span>
                  <span style={{ fontSize: 13, color: '#a78bfa', fontWeight: 700 }}>
                    {playRate}% played
                    <span style={{ fontSize: 11, color: '#8b949e', fontWeight: 400, marginLeft: 6 }}>{total.toLocaleString()} decisions</span>
                  </span>
                </div>
                <div style={{ background: '#21262d', borderRadius: 6, height: 12, overflow: 'hidden' }}>
                  <div style={{
                    width: `${playRate || 0}%`, height: '100%',
                    background: 'linear-gradient(90deg, #7c3aed88, #a78bfa)',
                    borderRadius: 6
                  }} />
                </div>
                <div style={{ fontSize: 10, color: '#484f58', marginTop: 4 }}>
                  How often you chose to play vs. sit when given the option
                </div>
              </div>
            );
          })()}
        </div>
      )}

      {/* ── Euchre Hand Performance ── */}
      {user && (
        <div style={{ background: '#161b22', borderRadius: 12, padding: isMobile ? 20 : 32, border: '1px solid #30363d', marginBottom: 24 }}>
          <h2 style={{ fontSize: isMobile ? 18 : 22, fontWeight: 700, marginBottom: isMobile ? 12 : 20, color: '#34d399' }}>
            Euchre — My Performance
          </h2>

          {euchreStats === null ? (
            <div style={{ color: '#8b949e', fontSize: 14 }}>Loading...</div>
          ) : !euchreStats || (euchreStats.totalHands ?? euchreStats.totalCalls) === 0 ? (
            <div style={{ color: '#8b949e', fontSize: 13, fontStyle: 'italic' }}>
              No Euchre hands recorded yet. Play some hands to see your stats here!
            </div>
          ) : (() => {
            const lonerPct = euchreStats.lonerAttempts > 0 ? Math.round((euchreStats.lonersMade / euchreStats.lonerAttempts) * 100) : 0;
            const rows = [
              { label: 'Hands Played', value: euchreStats.totalHands, note: 'total Euchre hands recorded', color: '#94a3b8' },
              { label: 'Calls', value: euchreStats.totalCalls, note: 'times you ordered/called trump', color: '#60a5fa' },
              { label: 'Made %', value: euchreStats.madePercent !== null ? `${euchreStats.madePercent}%` : '—', note: `${euchreStats.madeCount} of ${euchreStats.totalCalls} calls made`, color: '#4ade80' },
              { label: 'Euchred %', value: euchreStats.euchredPercent !== null ? `${euchreStats.euchredPercent}%` : '—', note: `${euchreStats.euchredCount} times euchred as caller`, color: '#f87171' },
              { label: 'Euchres Forced', value: euchreStats.euchresForced, note: 'times you euchred the calling team', color: '#fb923c' },
              { label: 'Loner Attempts', value: euchreStats.lonerAttempts, note: 'times you went alone', color: '#a78bfa' },
              { label: 'Loners Made', value: euchreStats.lonersMade, note: `${lonerPct}% success rate`, color: '#f0abfc' },
            ];
            return (
              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(4, 1fr)', gap: 12 }}>
                {rows.map(({ label, value, note, color }) => (
                  <div key={label} style={{ background: '#0d1117', borderRadius: 8, padding: '12px 14px', border: '1px solid #21262d' }}>
                    <div style={{ fontSize: 11, color: '#8b949e', marginBottom: 4 }}>{label}</div>
                    <div style={{ fontSize: isMobile ? 22 : 28, fontWeight: 800, color, lineHeight: 1 }}>{value ?? '—'}</div>
                    <div style={{ fontSize: 10, color: '#484f58', marginTop: 4 }}>{note}</div>
                  </div>
                ))}
              </div>
            );
          })()}
        </div>
      )}

      {/* ── Euchre Partner Performance ── */}
      {euchreStats?.partnerStats?.length > 0 && (
        <div style={{ background: '#161b22', borderRadius: 12, padding: isMobile ? 20 : 32, border: '1px solid #30363d', marginBottom: 24 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: isMobile ? 12 : 20, flexWrap: 'wrap', gap: 8 }}>
            <h2 style={{ fontSize: isMobile ? 18 : 22, fontWeight: 700, color: '#34d399', margin: 0 }}>
              Euchre — With Partner
            </h2>
            <select
              value={selectedPartner}
              onChange={e => setSelectedPartner(e.target.value)}
              style={{ background: '#21262d', border: '1px solid #30363d', borderRadius: 6, color: '#c9d1d9', fontSize: 13, padding: '6px 10px', cursor: 'pointer' }}
            >
              {euchreStats.partnerStats.map(p => (
                <option key={p.partnerId} value={p.partnerId}>
                  {p.partnerId}{p.partnerIsAi ? ' (AI)' : ''}
                </option>
              ))}
            </select>
          </div>

          {(() => {
            const p = euchreStats.partnerStats.find(x => x.partnerId === selectedPartner);
            if (!p) return null;
            const pLonerPct = p.lonerAttempts > 0 ? Math.round((p.lonersMade / p.lonerAttempts) * 100) : 0;
            const rows = [
              { label: 'Hands Together', value: p.hands, note: 'total hands as partners', color: '#94a3b8' },
              { label: 'Calls', value: p.calls, note: 'times you called trump together', color: '#60a5fa' },
              { label: 'Made %', value: p.madePercent !== null ? `${p.madePercent}%` : '—', note: `${p.madeCount} of ${p.calls} calls made`, color: '#4ade80' },
              { label: 'Euchred %', value: p.euchredPercent !== null ? `${p.euchredPercent}%` : '—', note: `${p.euchredCount} times euchred as caller`, color: '#f87171' },
              { label: 'Euchres Forced', value: p.euchresForced, note: 'opponents euchred together', color: '#fb923c' },
              { label: 'Loner Attempts', value: p.lonerAttempts, note: 'times you went alone', color: '#a78bfa' },
              { label: 'Loners Made', value: p.lonersMade, note: `${pLonerPct}% success rate`, color: '#f0abfc' },
            ];
            return (
              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(4, 1fr)', gap: 12 }}>
                {rows.map(({ label, value, note, color }) => (
                  <div key={label} style={{ background: '#0d1117', borderRadius: 8, padding: '12px 14px', border: '1px solid #21262d' }}>
                    <div style={{ fontSize: 11, color: '#8b949e', marginBottom: 4 }}>{label}</div>
                    <div style={{ fontSize: isMobile ? 22 : 28, fontWeight: 800, color, lineHeight: 1 }}>{value ?? '—'}</div>
                    <div style={{ fontSize: 10, color: '#484f58', marginTop: 4 }}>{note}</div>
                  </div>
                ))}
              </div>
            );
          })()}
        </div>
      )}

      {/* Share Stats */}
      <div style={{
        background: '#161b22', borderRadius: 12,
        padding: isMobile ? 20 : 32,
        border: '1px solid #30363d', marginBottom: 24
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: isMobile ? 12 : 20 }}>
          <h2 style={{ fontSize: isMobile ? 18 : 22, fontWeight: 700, color: '#58a6ff', margin: 0 }}>
            Share Stats
          </h2>
          <button onClick={() => setShowStatCard(!showStatCard)} style={{
            background: showStatCard ? '#21262d' : '#238636', color: '#fff',
            border: 'none', borderRadius: 6, padding: '8px 16px',
            fontSize: 13, fontWeight: 600, cursor: 'pointer'
          }}>
            {showStatCard ? 'Hide' : 'Preview Card'}
          </button>
        </div>
        {showStatCard && (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16 }}>
            <div style={{ transform: isMobile ? 'scale(0.8)' : 'none', transformOrigin: 'top center' }}>
              <StatCard
                ref={statCardRef}
                user={user}
                stats={{
                  gamesPlayed: playerStats?.gameStats?.gamesPlayed || 0,
                  wins: playerStats?.gameStats?.wins || 0,
                  longestStreak: user?.longestWinStreak || 0
                }}
              />
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={async () => {
                if (!statCardRef.current) return;
                setShareStatus('generating');
                try {
                  const canvas = await html2canvas(statCardRef.current, { backgroundColor: null, scale: 2 });
                  const blob = await new Promise(resolve => canvas.toBlob(resolve, 'image/png'));
                  if (navigator.share && navigator.canShare?.({ files: [new File([blob], 'stat-card.png', { type: 'image/png' })] })) {
                    await navigator.share({
                      title: 'My Euchre Table Stats',
                      files: [new File([blob], 'euchre-stats.png', { type: 'image/png' })]
                    });
                    setShareStatus('shared');
                  } else {
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url; a.download = 'euchre-stats.png'; a.click();
                    URL.revokeObjectURL(url);
                    setShareStatus('downloaded');
                  }
                  setTimeout(() => setShareStatus(''), 3000);
                } catch {
                  setShareStatus('');
                }
              }} style={{
                background: '#238636', color: '#fff', border: 'none', borderRadius: 6,
                padding: '10px 20px', fontSize: 14, fontWeight: 600, cursor: 'pointer'
              }}>
                {shareStatus === 'generating' ? 'Generating...' : shareStatus === 'shared' ? 'Shared!' : shareStatus === 'downloaded' ? 'Downloaded!' : 'Share / Download'}
              </button>
            </div>
          </div>
        )}
        {!showStatCard && (
          <p style={{ color: '#8b949e', fontSize: 13, margin: 0 }}>
            Generate a branded stat card to share with friends.
          </p>
        )}
      </div>

      {/* Invite Friends */}
      {referralCode && (
        <div style={{
          background: '#161b22', borderRadius: 12,
          padding: isMobile ? 20 : 32,
          border: '1px solid #30363d', marginBottom: 24
        }}>
          <h2 style={{ fontSize: isMobile ? 18 : 22, fontWeight: 700, marginBottom: isMobile ? 12 : 20, color: '#3fb950' }}>
            Invite Friends
          </h2>
          <p style={{ color: '#8b949e', fontSize: 13, marginBottom: 16 }}>
            Share your code — you both earn 50 Bucks!
          </p>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
            <div style={{
              background: '#0d1117', border: '1px solid #30363d', borderRadius: 8,
              padding: '10px 16px', fontSize: isMobile ? 18 : 22, fontWeight: 700,
              letterSpacing: 3, color: '#fbbf24', fontFamily: 'monospace', flex: 1, textAlign: 'center'
            }}>
              {referralCode}
            </div>
            <button onClick={() => {
              navigator.clipboard.writeText(referralCode);
              setReferralCopied(true);
              setTimeout(() => setReferralCopied(false), 2000);
            }} style={{
              background: referralCopied ? '#238636' : '#21262d', color: referralCopied ? '#fff' : '#c9d1d9',
              border: '1px solid #30363d', borderRadius: 6, padding: '10px 16px',
              fontSize: 13, fontWeight: 600, cursor: 'pointer'
            }}>
              {referralCopied ? 'Copied!' : 'Copy'}
            </button>
            <button onClick={() => navigate('/referrals')} style={{
              background: '#238636', color: '#fff', border: 'none', borderRadius: 6,
              padding: '10px 16px', fontSize: 13, fontWeight: 600, cursor: 'pointer'
            }}>
              More
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
